/** Line icons (24×24, 2.2px stroke, round joins), drawn for this app. */

const PATHS = {
  path: 'M6 4h9a3 3 0 0 1 0 6H9a3 3 0 0 0 0 6h9M6 4a2 2 0 1 0 0 .01M18 16a2 2 0 1 0 0 .01',
  home: 'M3.5 11 12 4l8.5 7v9h-6v-6h-5v6h-6z',
  cards: 'M4 7.5A2.5 2.5 0 0 1 6.5 5h8A2.5 2.5 0 0 1 17 7.5v11a2.5 2.5 0 0 1-2.5 2.5h-8A2.5 2.5 0 0 1 4 18.5zM8 2.5h9.5A2.5 2.5 0 0 1 20 5v11',
  lock: 'M6.5 11h11v9h-11zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  text: 'M5 5h14M5 9.5h14M5 14h9M5 18.5h6',
  task: 'M9 4h6v3H9zM7 5.5H5.5V21h13V5.5H17M8.5 13l2.5 2.5 4.5-5',
  words: 'M6 3h10l3 3v15H6zM9 9h7M9 13h7M9 17h4',
  chat: 'M4 12a8 7 0 1 1 3.5 5.8L4 19.5l1.2-3.6A6.6 6.6 0 0 1 4 12z',
  league: 'M8 4h8v5a4 4 0 0 1-8 0zM8 6H5a3 3 0 0 0 3 4M16 6h3a3 3 0 0 1-3 4M12 13v4M8.5 20h7M10 17h4v3h-4z',
  profile: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.5 20a7.5 7.5 0 0 1 15 0',
  close: 'M6 6l12 12M18 6L6 18',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 6l6 6-6 6',
  back: 'M15 6l-6 6 6 6',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  plus: 'M12 5v14M5 12h14',
  speaker: 'M4 10v4h4l5 4V6L8 10zM16.5 9a4 4 0 0 1 0 6',
  send: 'M12 19V5M6 11l6-6 6 6',
  refresh: 'M19 8a7.5 7.5 0 0 0-13.4-1.6M5 4v3.5h3.5M5 16a7.5 7.5 0 0 0 13.4 1.6M19 20v-3.5h-3.5',
  book: 'M4 5.5C6.5 4 9.5 4 12 6c2.5-2 5.5-2 8-.5V19c-2.5-1.5-5.5-1.5-8 .5-2.5-2-5.5-2-8-.5zM12 6v13.5',
  sparkle: 'M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6z',
  pencil: 'M4 20l1-4L16 5l3 3L8 19zM14 7l3 3',
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  star: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.8l-5.2 2.8 1-5.8L3.5 9.7l5.9-.9z',
  moon: 'M19.5 14.5A8 8 0 0 1 9.5 4.5a8 8 0 1 0 10 10z',
  sun: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4',
  bolt: 'M13 3L5 13.5h6L10 21l8-10.5h-6z',
  books: 'M4 4h4v16H4zM9.5 4h4v16h-4zM15 5.2l3.6-.9 3 15.5-3.6.9z',
  camera: 'M4 8h3.5L9 5.5h6L16.5 8H20v11H4zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  trash: 'M5 7h14M10 7V4.5h4V7M7 7l1 13h8l1-13',
  offline: 'M3 3l18 18M8.5 8.8A9.5 9.5 0 0 0 4 11.5M2 8.5A14 14 0 0 1 6 6M12 18.5h.01M9 15a4.5 4.5 0 0 1 5.5-.6M16.5 11.4c1 .4 2 1 2.8 1.8M11 5.6A14 14 0 0 1 22 8.5',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon({ name, size = 24, label }: { name: IconName; size?: number; label?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/** Saffron flame for the streak (the only saffron besides the robo-cat's sparkle). */
export function Flame({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M8 1.5c.4 2.2 3.8 3.9 3.8 7.4A3.8 3.8 0 0 1 8 12.8a3.8 3.8 0 0 1-3.8-3.9c0-1.6.8-2.6 1.6-3.4.1 1 .6 1.8 1.4 2.1C7 5.4 7.2 3.4 8 1.5z"
        fill="var(--saffron)"
      />
    </svg>
  );
}
